import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONTACT_TYPES, CONTACT_GROUPS, CONTACT_PURPOSE, ENTITY_HINT, waUrl } from "@/lib/contacts";
import { fmtPhone } from "@/lib/format";
import { Search, Plus, Pencil, Trash2, MessageCircle, Mail } from "lucide-react";

const card = "bg-white rounded-[16px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100";
const icon = "p-1.5 rounded-md text-slate-500 hover:text-ec-green hover:bg-slate-100 transition-colors";

const TypeOptions = () => CONTACT_GROUPS.map((g) => (
  <SelectGroup key={g.label}>
    <SelectLabel className="text-[11px] uppercase tracking-wide text-slate-400">{g.label}</SelectLabel>
    {g.types.map((k) => <SelectItem key={k} value={k} data-testid={`contact-type-option-${k}`}>{CONTACT_TYPES[k]}</SelectItem>)}
  </SelectGroup>
));

function EditDialog({ c, onClose, onSaved }) {
  const [f, setF] = useState(c);
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      if (f.id) await api.put(`/contacts/${f.id}`, f); else await api.post("/contacts", f);
      toast.success("Contacto guardado"); onSaved();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="agency-contact-dialog">
        <DialogHeader><DialogTitle className="font-heading text-ec-green">{f.id ? "Editar contacto" : "Novo contacto"}</DialogTitle></DialogHeader>
        <div><Label className="text-xs">Tipo</Label>
          <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
            <SelectTrigger className="mt-1" data-testid="agency-contact-type"><SelectValue /></SelectTrigger>
            <SelectContent><TypeOptions /></SelectContent>
          </Select>
          {CONTACT_PURPOSE[f.type] && <p className="text-xs text-slate-500 mt-1.5" data-testid="agency-contact-purpose">Para quê: {CONTACT_PURPOSE[f.type]}</p>}
        </div>
        <div><Label className="text-xs">Entidade</Label><Input value={f.entity || ""} onChange={s("entity")} placeholder={ENTITY_HINT[f.type]} className="mt-1" data-testid="agency-contact-entity" /></div>
        <div><Label className="text-xs">Nome</Label><Input value={f.name || ""} onChange={s("name")} className="mt-1" data-testid="agency-contact-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Telefone</Label><Input value={f.phone || ""} onChange={s("phone")} className="mt-1" data-testid="agency-contact-phone" /></div>
          <div><Label className="text-xs">Email</Label><Input value={f.email || ""} onChange={s("email")} className="mt-1" data-testid="agency-contact-email" /></div>
        </div>
        <div className="flex justify-end"><button onClick={save} data-testid="agency-contact-save" className="px-4 py-2 rounded-lg bg-ec-green text-white font-semibold hover:bg-ec-greenDark transition-colors">Guardar</button></div>
      </DialogContent>
    </Dialog>
  );
}

function ContactLine({ c, onEdit, onDelete }) {
  const canManage = c.can_edit;
  return (
    <div className="flex items-center gap-2 py-3 border-t border-slate-100 first:border-t-0" data-testid={`agency-contact-${c.id}`}>
      <span title={CONTACT_TYPES[c.type]} className="hidden sm:inline-block text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 shrink-0 w-36 text-center truncate">{CONTACT_TYPES[c.type]}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] text-ec-green truncate">{[c.name, c.entity].filter(Boolean).join(" · ")}</div>
        <div className="sm:hidden text-[11px] text-slate-400 truncate" data-testid={`agency-contact-type-${c.id}`}>{CONTACT_TYPES[c.type]}</div>
        <div className="text-xs text-slate-500 truncate">{[CONTACT_PURPOSE[c.type] && CONTACT_PURPOSE[c.type].split(".")[0], fmtPhone(c.phone), c.email].filter(Boolean).join(" · ") || "Sem telefone nem email"}
          {c.used_in > 0 && ` · usado em ${c.used_in} processo${c.used_in > 1 ? "s" : ""}`}</div>
      </div>
      {c.phone && <a href={waUrl(c.phone)} target="_blank" rel="noreferrer" className={icon} title="WhatsApp"><MessageCircle size={15} /></a>}
      {c.email && <a href={`mailto:${c.email}`} className={icon} title="Email"><Mail size={15} /></a>}
      {canManage && <button onClick={onEdit} className={icon} title="Editar" data-testid={`agency-contact-edit-${c.id}`}><Pencil size={15} /></button>}
      {canManage && <button onClick={onDelete} className={`${icon} hover:text-red-600`} title="Apagar" data-testid={`agency-contact-delete-${c.id}`}><Trash2 size={15} /></button>}
    </div>
  );
}

export function AgencyContacts() {
  const [rows, setRows] = useState([]);
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  useEffect(() => { if (params.get("q") != null) setQ(params.get("q")); }, [params]);
  const [type, setType] = useState("todos");
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/contacts", { params: { q: q || undefined, type: type === "todos" ? undefined : type } }).then(({ data }) => setRows(data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, type]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (c) => {
    if (!window.confirm(`Apagar ${c.name || c.entity}? Deixa de aparecer nas sugestões; os processos onde foi usado mantêm o contacto.`)) return;
    try { await api.delete(`/contacts/${c.id}`); toast.success("Contacto apagado"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className={card} data-testid="agency-contacts">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <h3 className="font-heading text-lg text-ec-green mr-auto">Contactos da agência</h3>
        <button onClick={() => setEdit({ type: type === "todos" ? "banco" : type })} data-testid="agency-contact-add"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-ec-green text-white text-sm font-semibold hover:bg-ec-greenDark transition-colors"><Plus size={14} /> Adicionar</button>
      </div>
      <p className="text-sm text-slate-500 mb-4">Bancos, avaliadores, condomínios, solicitadores e intermediários (sugeridos nos processos) e os seus contactos de preparação do imóvel: fotógrafo, planta, home staging e perito do certificado energético.</p>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar por nome, entidade, telefone ou email" className="pl-9" data-testid="agency-contacts-search" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="sm:w-56" data-testid="agency-contacts-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os tipos</SelectItem>
            <TypeOptions />
          </SelectContent>
        </Select>
      </div>
      {rows.length === 0 && <div className="text-sm text-slate-500 py-4" data-testid="agency-contacts-empty">Sem contactos{q || type !== "todos" ? " para esta pesquisa" : " ainda"}.</div>}
      {rows.map((c) => <ContactLine key={c.id} c={c} onEdit={() => setEdit(c)} onDelete={() => del(c)} />)}
      {edit && <EditDialog c={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
